import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { ArrowBack, Cancel } from "@mui/icons-material";
import api from "../../api/axios";
import LoadingScreen from "../../components/LoadingScreen";
import SortableHeader from "../../components/SortableHeader";
import { useSortable } from "../../hooks/useSortable";
import { useSearchFilter } from "../../hooks/useSearchFilter";

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

export default function AgentCancellationRate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allDecisions, setAllDecisions] = useState([]);

  const { sortKey, sortDir, handleSort, applySort } = useSortable();
  const { searchTerm, setSearchTerm, applyFilter } = useSearchFilter();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: 1, limit: 500 });
        const { data } = await api.get(`/agent/notification-decisions?${params}`);
        setAllDecisions(data.decisions || []);
      } catch (err) {
        console.error("Failed to fetch notification decisions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const declinedOnly = useMemo(() => allDecisions.filter(d => d.status === "DECLINED"), [allDecisions]);

  const sortConfig = useMemo(() => ({
    sentAt: { getValue: (d) => new Date(d.sentAt || 0).getTime() },
    vendorName: { getValue: (d) => (d.vendorName || "").toLowerCase() },
    totalAmount: { getValue: (d) => Number(d.totalAmount) || 0 },
    commission: { getValue: (d) => Number(d.commission) || 0 },
    respondedAt: { getValue: (d) => new Date(d.respondedAt || d.sentAt || 0).getTime() },
  }), []);

  const searchConfig = useMemo(() => [
    { getValue: (d) => d.vendorName },
    { getValue: (d) => d.vendorCity },
    { getValue: (d) => d.totalAmount },
    { getValue: (d) => d.commission },
    { getValue: (d) => d.sentAt && new Date(d.sentAt).toLocaleDateString("en-IN") },
    { getValue: (d) => d.respondedAt && new Date(d.respondedAt || d.sentAt).toLocaleDateString("en-IN") },
  ], []);

  const filteredDeclined = useMemo(() => applyFilter(declinedOnly, searchConfig), [declinedOnly, searchConfig, applyFilter]);
  const sortedDeclined = useMemo(() => applySort(filteredDeclined, sortConfig), [filteredDeclined, sortConfig, applySort]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>📉 Cancellation Rate</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Orders you declined</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
              <Cancel sx={{ fontSize: 18, color: "#e11d48" }} />
              Declined Orders ({declinedOnly.length})
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="🔍 Filter orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mx: 2, mt: 1.5, mb: 0, "& input": { fontSize: "0.82rem" } }}
          />

          {loading ? (
            <LoadingScreen message="Loading cancellation data..." />
          ) : sortedDeclined.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(225,29,72,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}><Cancel sx={{ fontSize: 32, color: "#e11d48" }} /></Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No declined orders</Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>You haven't declined any order notifications yet.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <SortableHeader label="Date" column="sentAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Vendor" column="vendorName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Amount" column="totalAmount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Commission" column="commission" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Declined At" column="respondedAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDeclined.map((d, idx) => (
                    <TableRow key={`${d.orderId}-${idx}`} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                      <TableCell><Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", whiteSpace: "nowrap" }}>{formatDate(d.sentAt)}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{d.vendorName || "—"}</Typography>
                        {d.vendorCity && <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem", display: "block" }}>{d.vendorCity}</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{formatCurrency(d.totalAmount)}</Typography></TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: "#16a34a", fontSize: "0.78rem" }}>{formatCurrency(d.commission)}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem", whiteSpace: "nowrap" }}>{formatDate(d.respondedAt || d.sentAt)}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
}